import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { AlertCircle, CheckCircle, Check, X, Save, Eye, Image, Loader2 } from 'lucide-react';
import backendApi from '../../services/backendApi';
import useAnimatedModalClose from '../../hooks/useAnimatedModalClose';

const statusClass = status => status === 'Validé'
  ? 'bg-green-100 text-green-800'
  : status === 'Échec' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800';

const fileAsScreenshot = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve({ type: file.type, data: String(reader.result).split(',')[1] || '' });
  reader.onerror = () => reject(new Error('Impossible de lire la capture'));
  reader.readAsDataURL(file);
});

const draftsFrom = criteria => Object.fromEntries(criteria.map(row => [
  row.id,
  { status: row.status, error: row.error, screenshot: undefined },
]));

const withCriterionStatus = (current, rowId, status, draft = {}) => {
  const criteria = current.criteria.map(row => row.id === rowId ? {
    ...row,
    status,
    ...(status === 'Échec' ? {
      error: draft.error || '',
      hasScreenshot: Boolean(draft.screenshot || row.hasScreenshot),
    } : {}),
  } : row);
  return {
    ...current,
    criteria,
    progress: {
      total: criteria.length,
      validated: criteria.filter(row => row.status === 'Validé').length,
    },
  };
};

export default function AcceptanceCriteriaGrid({ task, editable = false, onChanged, onAvailability, fallback = null, initialData, initialError = null }) {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [screenshots, setScreenshots] = useState({});
  const [rejectingId, setRejectingId] = useState(null);
  const [preview, setPreview] = useState(null);
  const previewModalExit = useAnimatedModalClose(() => setPreview(null));

  const load = useCallback(async () => {
    if (!task?.id) return;
    setError('');
    try {
      if (initialData === null) { setError(initialError || ''); return; }
      const next = initialData === undefined ? await backendApi.getAcceptanceCriteria(task.id) : initialData;
      setData(next);
      onAvailability?.(next.criteria.length > 0);
      setDrafts(draftsFrom(next.criteria));
    } catch (loadError) {
      setError(loadError.message);
    }
  }, [task?.id, onAvailability, initialData, initialError]);

  useEffect(() => { setData(null); setRejectingId(null); setScreenshots({}); setPreview(null); load(); }, [load]);

  const isDone = useMemo(() => ['done', 'terminé'].some(label =>
    String(data?.task?.stageName || task?.category || '').toLowerCase().includes(label)
  ) || [132, 305].includes(data?.task?.stageId || task?.stageId), [data, task]);
  const canEdit = editable;

  const applyResponse = next => {
    setData(next);
    setDrafts(draftsFrom(next.criteria));
    // La grille possède déjà la réponse Odoo complète. Le rafraîchissement des
    // autres vues ne doit donc pas prolonger le chargement de cette action.
    try {
      const notification = onChanged?.(next);
      if (notification?.catch) notification.catch(() => {});
    } catch (_) {
      // Une erreur de synchronisation de la vue parente ne doit pas transformer
      // une écriture Odoo réussie en faux échec pour l'utilisateur.
    }
  };

  const save = async (row, status) => {
    const draft = drafts[row.id];
    if (!draft) return;
    const nextStatus = status || 'Échec';
    const previousData = data;
    const previousDrafts = drafts;
    const optimisticData = withCriterionStatus(data, row.id, nextStatus, draft);
    setBusy(row.id); setError('');
    setData(optimisticData);
    setDrafts(draftsFrom(optimisticData.criteria));
    if (nextStatus === 'Échec') setRejectingId(null);
    try {
      const values = nextStatus === 'Échec' ? { ...draft, status: nextStatus } : { status: nextStatus };
      const next = await backendApi.updateAcceptanceCriterion(task.id, row.id, values);
      if (draft.screenshot && nextStatus !== 'Validé') {
        setScreenshots(current => ({ ...current, [row.id]: `data:${draft.screenshot.type};base64,${draft.screenshot.data}` }));
      }
      setRejectingId(null);
      applyResponse(next);
    } catch (saveError) {
      setData(previousData);
      setDrafts(previousDrafts);
      if (nextStatus === 'Échec') setRejectingId(row.id);
      setError(saveError.message);
    }
    finally { setBusy(null); }
  };

  const validateAll = async () => {
    const previousData = data;
    const optimisticData = {
      ...data,
      criteria: data.criteria.map(row => ({ ...row, status: 'Validé' })),
      progress: { validated: data.criteria.length, total: data.criteria.length },
    };
    setBusy('all'); setError('');
    setData(optimisticData);
    setDrafts(draftsFrom(optimisticData.criteria));
    try { applyResponse(await backendApi.validateAllAcceptanceCriteria(task.id)); }
    catch (saveError) {
      setData(previousData);
      setDrafts(draftsFrom(previousData.criteria));
      setError(saveError.message);
    }
    finally { setBusy(null); }
  };

  const chooseFile = async (rowId, file) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 10 * 1024 * 1024) {
      setError('La capture doit être une image PNG, JPEG ou WebP de 10 Mo maximum.');
      return;
    }
    try {
      const screenshot = await fileAsScreenshot(file);
      setDrafts(current => ({ ...current, [rowId]: { ...current[rowId], screenshot } }));
    } catch (fileError) { setError(fileError.message); }
  };

  const showScreenshot = async row => {
    if (screenshots[row.id]) return setPreview({ src: screenshots[row.id], scenario: row.scenario });
    setBusy(`image-${row.id}`); setError('');
    try {
      const image = await backendApi.getAcceptanceCriterionScreenshot(task.id, row.id);
      const src = `data:${image.type};base64,${image.data}`;
      setScreenshots(current => ({ ...current, [row.id]: src }));
      setPreview({ src, scenario: row.scenario });
    } catch (imageError) { setError(imageError.message); }
    finally { setBusy(null); }
  };

  if (!data && !error) return <div className="flex items-center gap-2 py-4 text-sm text-gray-500"><span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-brand-purple" aria-hidden="true" />Chargement des critères…</div>;
  if (!data) return <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>;
  if (!data.criteria.length) return fallback;

  const failedCount = data.criteria.filter(row => row.status === 'Échec').length;

  return (
    <section className="acceptance-grid mb-6 min-w-0" aria-label="Critères d’acceptation">
      <div className="acceptance-grid__header">
        <h3>Critères d’acceptation</h3>
        <div className="acceptance-grid__progress" aria-label={`${data.progress.validated} critères validés sur ${data.progress.total}`}>
          <span className="sr-only">{data.progress.validated} / {data.progress.total} CA validés</span>
          <span className="acceptance-grid__progress-bars" aria-hidden="true">
            {data.criteria.map(row => <i key={row.id} className={`is-${row.status === 'Validé' ? 'valid' : row.status === 'Échec' ? 'failed' : 'pending'}`} />)}
          </span>
          <span><strong>{data.progress.validated} sur {data.progress.total}</strong> critères validés</span>
          {failedCount > 0 && <em>· {failedCount} en échec</em>}
        </div>
      </div>
      {error && <div className="mb-3 flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"><AlertCircle className="h-4 w-4 shrink-0" />{error}</div>}
      <div className="acceptance-grid__list">
        {data.criteria.map((row, index) => {
          const draft = drafts[row.id] || { status: row.status, error: row.error };
          return <article key={row.id} className={`acceptance-card is-${row.status === 'Validé' ? 'valid' : row.status === 'Échec' ? 'failed' : 'pending'}`}>
            <div className="acceptance-card__number" aria-hidden="true">{index + 1}</div>
            <div className="acceptance-card__content">
              <h4>{row.scenario}</h4>
              {row.description && <p className="acceptance-card__description">{row.description}</p>}
              <div className="acceptance-card__expected">
                <strong>Résultat attendu</strong>
                <p>{row.expectedResult || '—'}</p>
              </div>
            </div>
            <div className="acceptance-card__status">
              {canEdit ? <div className="acceptance-card__choice" role="group" aria-label={`Résultat de ${row.scenario}`}>
                  <button type="button" aria-label={`${row.status === 'Validé' ? 'Annuler la validation de' : 'Accepter'} ${row.scenario}`}
                    disabled={busy !== null || (isDone && row.status !== 'Validé')}
                    aria-pressed={row.status === 'Validé'} onClick={() => save(row, row.status === 'Validé' ? 'À tester' : 'Validé')} className="is-valid">
                    <Check className="h-4 w-4" /> Validé
                  </button>
                  <button type="button" aria-label={`${row.status === 'Échec' ? 'Annuler le refus de' : 'Refuser'} ${row.scenario}`}
                    disabled={busy !== null || (isDone && row.status !== 'Échec')}
                    aria-pressed={row.status === 'Échec'}
                    onClick={() => {
                      if (row.status === 'Échec') { save(row, 'À tester'); return; }
                      setRejectingId(row.id);
                      setDrafts(current => ({ ...current, [row.id]: { status: 'Échec', error: row.error || '', screenshot: undefined } }));
                    }} className="is-failed">
                    <X className="h-4 w-4" /> Échec
                  </button>
                </div> : <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusClass(row.status)}`}>{row.status}</span>}
            </div>
            {(rejectingId === row.id || (row.status === 'Échec' && (row.error || row.hasScreenshot))) && <div className="acceptance-card__failure">
              {canEdit && rejectingId === row.id ? <div className="grid gap-3 md:grid-cols-2">
                <textarea aria-label={`Description de l’erreur de ${row.scenario}`} value={draft.error || ''}
                  onChange={event => setDrafts(current => ({ ...current, [row.id]: { ...current[row.id], error: event.target.value } }))}
                  placeholder="Décrivez ce qui ne fonctionne pas…" rows="2" className="rounded-md border border-red-200 px-3 py-2 text-sm" />
                <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-red-300 bg-white px-3 py-2 text-sm text-red-700">
                  <Image className="h-4 w-4" />{draft.screenshot ? 'Capture sélectionnée' : 'Ajouter une capture (facultatif)'}
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={event => chooseFile(row.id, event.target.files?.[0])} />
                </label>
              </div> : row.error && <p className="whitespace-pre-wrap text-sm text-red-800">{row.error}</p>}
              {rejectingId === row.id && draft.screenshot && <button type="button" aria-label={`Agrandir la capture du refus de ${row.scenario}`}
                onClick={() => setPreview({ src: `data:${draft.screenshot.type};base64,${draft.screenshot.data}`, scenario: row.scenario })}
                className="mt-3 block cursor-zoom-in rounded focus-visible:ring-2 focus-visible:ring-brand-purple">
                <img src={`data:${draft.screenshot.type};base64,${draft.screenshot.data}`} alt="Aperçu de la capture du refus" className="max-h-80 rounded border border-red-200" />
              </button>}
              {row.hasScreenshot && <button type="button" onClick={() => showScreenshot(row)} className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-red-700 hover:underline">
                {busy === `image-${row.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Voir la capture
              </button>}
              {screenshots[row.id] && !(rejectingId === row.id && draft.screenshot) && <button type="button" aria-label={`Agrandir la capture de ${row.scenario}`}
                onClick={() => setPreview({ src: screenshots[row.id], scenario: row.scenario })}
                className="mt-3 block cursor-zoom-in rounded focus-visible:ring-2 focus-visible:ring-brand-purple">
                <img src={screenshots[row.id]} alt={`Capture du critère ${row.scenario}`} className="max-h-80 rounded border border-red-200" />
              </button>}
            </div>}
            {canEdit && rejectingId === row.id && <div className="acceptance-card__failure-actions">
              <button type="button" onClick={() => setRejectingId(null)} disabled={busy !== null} className="rounded-md px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100">Annuler</button>
              <button type="button" aria-label={`Enregistrer le refus de ${row.scenario}`} onClick={() => save(row)} disabled={busy !== null}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {busy === row.id ? <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> : <Save className="mr-2 inline h-4 w-4" />}Enregistrer le refus
            </button></div>}
          </article>;
        })}
      </div>
      {canEdit && !isDone && data.progress.validated < data.progress.total && (
        <div className="acceptance-grid__footer">
          <button type="button" onClick={validateAll} disabled={busy !== null} className="acceptance-grid__validate-all">
            {busy === 'all' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />} Tout valider
          </button>
        </div>
      )}
      <Dialog open={preview !== null} onClose={previewModalExit.close} className={`relative z-[200] ${previewModalExit.modalClassName}`}>
        <div className="fixed inset-0 bg-black/70" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4 sm:p-8">
          <Dialog.Panel className="dashboard-modal__surface flex max-h-full w-full max-w-7xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-5 py-4">
              <div className="min-w-0">
                <Dialog.Title className="font-semibold text-gray-900">Capture du motif de refus</Dialog.Title>
                <p className="truncate text-sm text-gray-500">{preview?.scenario}</p>
              </div>
              <button type="button" aria-label="Fermer la prévisualisation" onClick={previewModalExit.close}
                className="shrink-0 rounded-lg p-2 text-gray-600 hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-brand-purple"><X className="h-5 w-5" /></button>
            </div>
            <div className="min-h-0 overflow-auto bg-gray-100 p-3 sm:p-6">
              {preview && <img src={preview.src} alt={`Capture du motif de refus — ${preview.scenario}`} className="mx-auto max-h-[75vh] max-w-full object-contain" />}
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </section>
  );
}
